/**
 * Utilidades para formatear texto en facturas
 */

/**
 * Capitaliza la marca (primera letra en mayúscula, resto en minúscula)
 * Si es null/undefined/vacío, retorna 'Genérica'
 */
export const capitalizarMarca = (marca: string | null | undefined): string => {
  if (!marca || marca.trim() === '') return 'Genérica';
  const trimmed = marca.trim().toLowerCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

/**
 * Capitaliza un nombre de ingrediente (title case - cada palabra con mayúscula)
 */
export const capitalizarIngrediente = (texto: string): string => {
  if (!texto) return '';
  return texto
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Infiere la categoría de un ingrediente basándose en keywords
 */
export const inferirCategoriaIngrediente = (nombre: string): string => {
  const nombreLower = nombre.toLowerCase();

  const categorias: { [key: string]: string[] } = {
    Carnes: [
      'carne',
      'pollo',
      'cerdo',
      'vacuno',
      'res',
      'lomo',
      'vienesa',
      'salchicha',
      'chorizo',
      'jamón',
      'pavo',
      'longaniza',
    ],
    Proteínas: ['pescado', 'atún', 'salmón', 'merluza', 'reineta'],
    Verduras: [
      'tomate',
      'lechuga',
      'zanahoria',
      'cebolla',
      'pimiento',
      'pepino',
      'papa',
      'arveja',
      'choclo',
      'espinaca',
      'zapallo',
      'brócoli',
      'coliflor',
      'apio',
    ],
    Lácteos: [
      'leche',
      'queso',
      'yogur',
      'crema',
      'mantequilla',
      'quesillo',
      'parmesano',
      'gauda',
      'mozzarella',
    ],
    Granos: [
      'pan',
      'harina',
      'frac',
      'galleta',
      'arroz',
      'fideos',
      'maicena',
      'hallulla',
      'marraqueta',
      'masa',
      'porotos',
      'lentejas',
      'tallarines',
      'avena',
      'quinoa',
    ],
    Condimentos: [
      'sal',
      'pimienta',
      'ajo',
      'orégano',
      'caldo',
      'mayonesa',
      'ketchup',
      'salsa',
      'comino',
      'vinagre',
      'mostaza',
      'ají',
      'merkén',
      'curry',
      'canela',
    ],
    Aceites: ['aceite', 'oliva', 'maravilla', 'girasol'],
    Bebidas: [
      'jugo',
      'agua',
      'bebida',
      'coca',
      'sprite',
      'té',
      'café',
      'fanta',
      'energética',
      'gaseosa',
    ],
    Frutas: [
      'manzana',
      'plátano',
      'naranja',
      'limón',
      'frutilla',
      'pera',
      'uva',
      'durazno',
      'melón',
      'sandía',
    ],
  };

  for (const [categoria, keywords] of Object.entries(categorias)) {
    if (keywords.some((kw) => nombreLower.includes(kw))) {
      return categoria;
    }
  }

  return 'Otros';
};

/**
 * Detecta y parsea información de packs en texto de descripción de producto
 * Retorna null si no detecta un pack, o un objeto con los datos del pack
 */
export interface PackInfo {
  cantidadPacks: number;
  unidadesPorPack: number;
  formatoOriginal: string;
}

export const detectarPack = (texto: string): PackInfo | null => {
  if (!texto) return null;

  const textoLimpio = texto.toLowerCase().trim();

  // Patrón 1: "pack 10", "pack de 10", "pack 10 unidades"
  const patronPack = /pack\s*(?:de\s*)?(\d+(?:\.\d+)?)/i;
  const matchPack = textoLimpio.match(patronPack);
  if (matchPack) {
    return {
      cantidadPacks: 1,
      unidadesPorPack: parseFloat(matchPack[1]),
      formatoOriginal: matchPack[0],
    };
  }

  // Patrón 2: "caja 12", "caja de 12"
  const patronCaja = /caja\s*(?:de\s*)?(\d+(?:\.\d+)?)/i;
  const matchCaja = textoLimpio.match(patronCaja);
  if (matchCaja) {
    return {
      cantidadPacks: 1,
      unidadesPorPack: parseFloat(matchCaja[1]),
      formatoOriginal: matchCaja[0],
    };
  }

  // Patrón 3: "2x10", "3×12", "2 x 10"
  const patronMultiplicador = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;
  const matchMult = textoLimpio.match(patronMultiplicador);
  if (matchMult) {
    return {
      cantidadPacks: parseFloat(matchMult[1]),
      unidadesPorPack: parseFloat(matchMult[2]),
      formatoOriginal: matchMult[0],
    };
  }

  // Patrón 4: "2 packs de 10", "3 cajas de 12"
  const patronMultiplePacks = /(\d+(?:\.\d+)?)\s*(?:packs?|cajas?)\s*(?:de\s*)?(\d+(?:\.\d+)?)/i;
  const matchMultiple = textoLimpio.match(patronMultiplePacks);
  if (matchMultiple) {
    return {
      cantidadPacks: parseFloat(matchMultiple[1]),
      unidadesPorPack: parseFloat(matchMultiple[2]),
      formatoOriginal: matchMultiple[0],
    };
  }

  return null;
};

/**
 * Aplica detección automática de pack a un item de factura OCR
 * Modifica el item si detecta un pack en el nombre o formato
 */
export const aplicarDeteccionPackAutomatica = (item: any): any => {
  // Si ya viene con formatoPack del OCR, no hacer nada
  if (item.formatoPack) {
    return item;
  }

  // Intentar detectar en el nombre del ingrediente
  const packInfo = detectarPack(item.nombreIngrediente);
  if (packInfo) {
    return {
      ...item,
      formatoPack: packInfo.formatoOriginal,
    };
  }

  return item;
};

/**
 * Formatea un número con separador de miles usando punto (formato chileno)
 * Ejemplo: 1000 -> "1.000", 10000 -> "10.000"
 */
export const formatearPrecioConPuntos = (valor: number | string): string => {
  // Convertir a número si es string
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;

  // Validar que sea un número válido
  if (isNaN(numero)) return '';

  // Usar toLocaleString con formato chileno (separador de miles: punto)
  return numero.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Parsea un string formateado con puntos a número
 * Ejemplo: "1.000" -> 1000, "10.000,50" -> 10000.50
 */
export const parsearPrecioFormateado = (valorFormateado: string): number => {
  if (!valorFormateado) return 0;

  // Remover puntos de miles y convertir coma decimal a punto
  const valorLimpio = valorFormateado
    .replace(/\./g, '') // Remover puntos de miles
    .replace(/,/g, '.'); // Convertir coma decimal a punto

  const numero = parseFloat(valorLimpio);
  return isNaN(numero) ? 0 : numero;
};
